import { CustomFieldConfig } from "@dreambigger/shared/src/types";
import type { DisclosureLogEventType } from "../api/use-disclosure-logs";

const isTruthy = (value: unknown): boolean =>
  value === true || value === "true";

type RecordEvent = (
  applicationId: string,
  args: {
    financialDisclosureSlug: string;
    eventType: DisclosureLogEventType;
    meta?: { stepSlug?: string; fieldKey?: string };
  }
) => void;

/**
 * After a step save has persisted, record the decision for each disclosure field
 * on the step from its saved checkbox value.
 *
 * Fired client-side *after* the save returns (not from CustomField) so the log
 * write follows the authoritative response. The checkbox itself lives in
 * meta.responses, which is the functional source of truth — this log adds the
 * defensible detail (pinned version, IP, server time). A dropped write therefore
 * degrades audit detail but never loses the fact of consent.
 *
 * Only fields the applicant was actually shown get a decision: a field dropped by
 * a display condition is absent from `fields`, and "not asked" is not "declined".
 */
export function recordDisclosureDecisions({
  applicationId,
  stepSlug,
  customFields,
  fields,
  recordEvent,
}: {
  applicationId: string;
  stepSlug: string;
  customFields?: CustomFieldConfig[];
  fields: Record<string, unknown>;
  recordEvent: RecordEvent;
}): void {
  (customFields ?? []).forEach((field) => {
    if (field.fieldType !== "disclosure") return;
    if (!(field.fieldKey in fields)) return;
    recordEvent(applicationId, {
      financialDisclosureSlug: field.financialDisclosureSlug,
      eventType: isTruthy(fields[field.fieldKey]) ? "agreed" : "declined",
      meta: { stepSlug, fieldKey: field.fieldKey },
    });
  });
}
